import { useNavigate } from "react-router-dom"
import { AmountCard, StatCard } from "../../common/page_component/index.js"
import { formatCurrency } from "../../../utils/formatCurrency.js"
import { hasExternalCommissionAccess, hasInternalCommissionDashboardAccess, hasMlAgentCommissionAccess } from "../../../utils/networkMode.js"
import { IndianRupee, AlertTriangle, FileText, Percent } from "lucide-react"
import RecentRecordStrip, { RecentRecordChip } from "./RecentRecordStrip.js"


export function getCommissionInsights(commissionData) {
  return {
    internal: {},
    mlAgent: commissionData?.ml_agent?.insights || {},
    external: commissionData?.external?.insights || {},
    internalRecent: [],
    mlAgentRecent: commissionData?.ml_agent?.recent_records || [],
    externalRecent: commissionData?.external?.recent_records || [],
  }
}

export function CommissionRecentSection({ user, externalRecent, mlAgentRecent }) {
  const navigate = useNavigate()
  const showMl = hasInternalCommissionDashboardAccess(user)
  const showExternal = hasExternalCommissionAccess(user)
  if (!showMl && !showExternal) return null

  return (
    <div className={`grid grid-cols-1 ${showMl && showExternal ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-4 md:gap-6 mb-4`}>
      {showMl && (
        <RecentRecordStrip
          title="Recent Unpaid Agent Commissions"
          viewAllLink="/company-settlement-approvals"
          items={mlAgentRecent || []}
          renderItem={(c) => (
            <RecentRecordChip
              key={c.id}
              title={c.agent_name || c.agent_user_id}
              badge={c.status || 'UNPAID'}
              badgeClass="text-red-600"
              subtitle={`${c.ml_sheet_id} · ${formatCurrency(c.commission_amount)}`}
              onClick={() => navigate('/company-settlement-approvals')}
            />
          )}
          emptyMsg="No unpaid agent commissions"
        />
      )}
      {showExternal && (
        <RecentRecordStrip
          title="Recent External Commissions"
          viewAllLink="/external-commissions"
          items={externalRecent || []}
          renderItem={(c) => (
            <RecentRecordChip
              key={c.external_commission_id}
              title={c.external_commission_id}
              badge={c.settlement_status}
              badgeClass="text-gray-500"
              subtitle={`${c.vehicle_owner_name || 'Owner'} · ${formatCurrency(c.balance_freight)}`}
              onClick={() => navigate(`/external-commissions/view/${c.external_commission_id}`)}
            />
          )}
          emptyMsg="No recent external commissions"
        />
      )}
    </div>
  )
}

export function CommissionSummaryCards({ user, external, mlAgent }) {
  const navigate = useNavigate()
  const cards = []

  if (hasMlAgentCommissionAccess(user)) {
    cards.push(
      <AmountCard
        key="ml-agent"
        label="Agent Commission"
        amount={mlAgent?.total_amount || 0}
        icon={<Percent className="w-5 h-5 text-white" />}
        color="bg-teal-600"
        subAmount={mlAgent?.paid_amount || 0}
        subLabel="Paid"
        subAmount2={mlAgent?.unpaid_amount || 0}
        subLabel2="Unpaid"
        onClick={() => navigate('/agent-commission-breakdown')}
      />
    )
  }

  if (hasExternalCommissionAccess(user)) {
    const earned = external?.total_agent_commission
      ?? external?.net_receivable_from_owner
      ?? 0
    const paid = external?.agent_commission_paid
      ?? external?.amount_received_from_owner
      ?? 0
    const due = external?.agent_commission_due
      ?? Math.max(0, Number(earned || 0) - Number(paid || 0))

    cards.push(
      <AmountCard
        key="external"
        label="External Commission"
        amount={earned}
        icon={<FileText className="w-5 h-5 text-white" />}
        color="bg-rose-600"
        subAmount={paid}
        subLabel="Paid"
        subAmount2={due}
        subLabel2="Due"
        onClick={() => navigate('/external-commission-breakdown')}
      />
    )
  }

  if (cards.length === 0) return null
  return cards
}

export function CommissionSectionCards({ user, mlAgent, external }) {
  const showMl = hasInternalCommissionDashboardAccess(user)
  const showExternal = hasExternalCommissionAccess(user)
  if (!showMl && !showExternal) return null

  return (
    <div className="space-y-4 mb-4">
      {showMl && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
          <AmountCard label="Total Commission" amount={mlAgent?.total_amount || 0} icon={<Percent className="w-5 h-5 text-white" />} color="bg-teal-600" />
          <AmountCard label="Paid" amount={mlAgent?.paid_amount || 0} icon={<IndianRupee className="w-5 h-5 text-white" />} color="bg-emerald-600" />
          <AmountCard label="Unpaid" amount={mlAgent?.unpaid_amount || 0} icon={<AlertTriangle className="w-5 h-5 text-white" />} color="bg-red-500" />
          <StatCard label="Paid Count" value={mlAgent?.paid_count || 0} icon={<FileText className="w-5 h-5 text-white" />} color="bg-green-600" />
          <StatCard label="Unpaid Count" value={mlAgent?.unpaid_count || 0} icon={<FileText className="w-5 h-5 text-white" />} color="bg-orange-500" />
          <StatCard label="Total Entries" value={mlAgent?.total_count || 0} icon={<FileText className="w-5 h-5 text-white" />} color="bg-blue-600" />
        </div>
      )}
      {showExternal && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
          <AmountCard label="Total Freight" amount={external?.total_freight || 0} icon={<IndianRupee className="w-5 h-5 text-white" />} color="bg-blue-600" />
          <AmountCard label="Net Receivable" amount={external?.net_receivable_from_owner || 0} icon={<IndianRupee className="w-5 h-5 text-white" />} color="bg-green-600" />
          <AmountCard label="Received from Owner" amount={external?.amount_received_from_owner || 0} icon={<IndianRupee className="w-5 h-5 text-white" />} color="bg-teal-600" />
          <AmountCard label="Paid to Owner" amount={external?.amount_paid_to_owner || 0} icon={<IndianRupee className="w-5 h-5 text-white" />} color="bg-purple-500" />
          <AmountCard label="Settlement Balance" amount={external?.settlement_balance || 0} icon={<AlertTriangle className="w-5 h-5 text-white" />} color="bg-red-500" />
          <AmountCard
            label="Agent Commission"
            amount={external?.total_agent_commission || 0}
            icon={<Percent className="w-5 h-5 text-white" />}
            color="bg-rose-600"
            subAmount={external?.agent_commission_paid || 0}
            subLabel="Paid"
            subAmount2={external?.agent_commission_due || 0}
            subLabel2="Due"
          />
        </div>
      )}
    </div>
  )
}

export function CommissionOverviewSections({ user, mlAgent, external }) {
  return <CommissionSectionCards user={user} mlAgent={mlAgent} external={external} />
}
